import { useState, useEffect } from "react";
import { LLMProviderForm, LLMProviderFormData } from "@/components/configuracion/llm-provider-form";
import { LLMRoleAssignments } from "@/components/configuracion/llm-role-assignments";
import { LLMUsageChart } from "@/components/configuracion/llm-usage-chart";
import { useAuth } from "@/components/providers/auth-provider";
import { useLLMUsageStats } from "@/hooks/use-llm-config";
import { fetchWithAuth } from "@/lib/api/client";

// Types matching backend schemas
type ProviderName = "openai" | "anthropic" | "google" | "azure_openai";
type HealthStatus = "healthy" | "degraded" | "unhealthy" | "unknown";

interface LLMProvider {
  id: string;
  provider_name: ProviderName;
  display_name: string;
  api_key_masked: string;
  api_base_url?: string;
  default_model: string;
  max_tokens_limit: number;
  temperature_default: number;
  is_active: boolean;
  is_default: boolean;
  priority: number;
  rate_limit_rpm: number;
  monthly_budget_usd?: number;
  monthly_usage_usd: number;
  budget_remaining_usd?: number;
  budget_usage_percent?: number;
  last_health_check?: string;
  health_status: HealthStatus;
  created_by_id: string;
  created_at: string;
  updated_at: string;
}

interface LLMProviderListResponse {
  data: LLMProvider[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const llmApi = {
  listProviders: async (page: number = 1, pageSize: number = 20): Promise<LLMProviderListResponse> => {
    return fetchWithAuth(`/api/v1/llm/providers?page=${page}&page_size=${pageSize}`);
  },

  createProvider: async (data: LLMProviderFormData): Promise<LLMProvider> => {
    return fetchWithAuth("/api/v1/llm/providers", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  setDefault: async (providerId: string): Promise<LLMProvider> => {
    return fetchWithAuth(`/api/v1/llm/providers/${providerId}/default`, {
      method: "PUT",
    });
  },

  updateProvider: async (providerId: string, data: Partial<LLMProviderFormData>): Promise<LLMProvider> => {
    return fetchWithAuth(`/api/v1/llm/providers/${providerId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  deleteProvider: async (providerId: string): Promise<void> => {
    return fetchWithAuth(`/api/v1/llm/providers/${providerId}`, {
      method: "DELETE",
    });
  },

  testProvider: async (providerId: string): Promise<any> => {
    return fetchWithAuth(`/api/v1/llm/providers/${providerId}/test`, {
      method: "POST",
    });
  },
};

function getHealthStatusColor(status: HealthStatus): string {
  switch (status) {
    case "healthy":
      return "text-green-600 bg-green-100";
    case "degraded":
      return "text-yellow-600 bg-yellow-100";
    case "unhealthy":
      return "text-red-600 bg-red-100";
    default:
      return "text-gray-600 bg-gray-100";
  }
}

function getHealthStatusText(status: HealthStatus): string {
  switch (status) {
    case "healthy":
      return "Saludable";
    case "degraded":
      return "Degradado";
    case "unhealthy":
      return "No saludable";
    default:
      return "Desconocido";
  }
}

interface TestResult {
  status: "healthy" | "unhealthy";
  latency_ms?: number;
  model_tested?: string;
  error?: string;
  tested_at: string;
}

export default function LLMConfigPage() {
  // Página reservada al operador de la plataforma (rol platform_admin).
  // El backend protege todos los endpoints con require_platform_admin;
  // aquí evitamos además disparar fetches que acabarían en 403.
  const { isPlatformAdmin } = useAuth();
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testingProvider, setTestingProvider] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<LLMProvider | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});

  // Cargar estadísticas de uso (solo si el rol permite verlas)
  const { data: usageStats, isLoading: isLoadingStats } = useLLMUsageStats("30d", isPlatformAdmin);

  useEffect(() => {
    if (isPlatformAdmin) {
      loadProviders();
    } else {
      setIsLoading(false);
    }
  }, [isPlatformAdmin]);

  const loadProviders = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await llmApi.listProviders();
      setProviders(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar proveedores");
      console.error("Error loading providers:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetDefault = async (providerId: string) => {
    try {
      await llmApi.setDefault(providerId);
      await loadProviders();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Error al cambiar proveedor por defecto");
    }
  };

  const handleDelete = async (providerId: string, displayName: string) => {
    if (!confirm(`¿Estás seguro de eliminar el proveedor "${displayName}"?`)) {
      return;
    }

    try {
      await llmApi.deleteProvider(providerId);
      await loadProviders();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Error al eliminar proveedor");
    }
  };

  const handleTestConnection = async (providerId: string) => {
    try {
      setTestingProvider(providerId);
      const result = await llmApi.testProvider(providerId);

      // Guardar resultado del test para mostrar en la UI
      setTestResults((prev) => ({
        ...prev,
        [providerId]: {
          ...result,
          tested_at: new Date().toISOString(),
        },
      }));

      await loadProviders();
    } catch (err) {
      // Guardar error como resultado fallido
      setTestResults((prev) => ({
        ...prev,
        [providerId]: {
          status: "unhealthy",
          error: err instanceof Error ? err.message : "Error al probar conexión",
          tested_at: new Date().toISOString(),
        },
      }));
    } finally {
      setTestingProvider(null);
    }
  };

  const handleCreateProvider = async (data: LLMProviderFormData) => {
    await llmApi.createProvider(data);
    await loadProviders();
    setIsFormOpen(false);
  };

  const handleUpdateProvider = async (data: LLMProviderFormData) => {
    if (!editingProvider) return;

    await llmApi.updateProvider(editingProvider.id, data);
    await loadProviders();
    setEditingProvider(null);
  };

  const handleEdit = (provider: LLMProvider) => {
    setEditingProvider(provider);
  };

  if (!isPlatformAdmin) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
          La configuración de proveedores de IA está reservada al operador de
          la plataforma. Si necesitas un cambio en los modelos o detectas un
          problema con la generación, contacta con soporte de LicitAI.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando proveedores LLM...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Configuración de Proveedores LLM</h1>
          <p className="text-sm text-gray-600 mt-1">
            Gestiona los proveedores de modelos de lenguaje para chat y análisis
          </p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nuevo Proveedor
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {providers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">No hay proveedores configurados</p>
          <button
            onClick={() => setIsFormOpen(true)}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Crear primer proveedor
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {providers.map((provider) => (
            <div
              key={provider.id}
              className={`bg-white rounded-lg shadow hover:shadow-lg transition-shadow border-2 ${
                provider.is_default ? "border-blue-500" : "border-transparent"
              }`}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {provider.display_name}
                    </h3>
                    <p className="text-sm text-gray-600">{provider.provider_name}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {provider.is_default && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                        Por defecto
                      </span>
                    )}
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${getHealthStatusColor(
                        provider.health_status
                      )}`}
                    >
                      {getHealthStatusText(provider.health_status)}
                    </span>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">API Key:</span>
                    <span className="text-gray-800 font-mono text-xs">{provider.api_key_masked}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Modelo:</span>
                    <span className="text-gray-800">{provider.default_model}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Prioridad:</span>
                    <span className="text-gray-800">{provider.priority}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Rate Limit:</span>
                    <span className="text-gray-800">{provider.rate_limit_rpm} RPM</span>
                  </div>
                </div>

                {/* Budget Info */}
                {provider.monthly_budget_usd && (
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">Presupuesto mensual:</span>
                      <span className="text-gray-800 font-medium">
                        ${provider.monthly_budget_usd.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">Usado:</span>
                      <span className="text-gray-800">
                        ${provider.monthly_usage_usd.toFixed(2)}
                      </span>
                    </div>
                    {provider.budget_usage_percent !== undefined && (
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            provider.budget_usage_percent > 90
                              ? "bg-red-500"
                              : provider.budget_usage_percent > 70
                              ? "bg-yellow-500"
                              : "bg-green-500"
                          }`}
                          style={{ width: `${Math.min(provider.budget_usage_percent, 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Test Result */}
                {testResults[provider.id] && (
                  <div
                    className={`mb-4 p-3 rounded-lg border ${
                      testResults[provider.id].status === "healthy"
                        ? "bg-green-50 border-green-200"
                        : "bg-red-50 border-red-200"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {testResults[provider.id].status === "healthy" ? (
                        <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${
                          testResults[provider.id].status === "healthy" ? "text-green-800" : "text-red-800"
                        }`}>
                          {testResults[provider.id].status === "healthy" ? "Conexión exitosa" : "Conexión fallida"}
                        </p>
                        {testResults[provider.id].status === "healthy" ? (
                          <p className="text-xs text-green-600 mt-1">
                            Latencia: {testResults[provider.id].latency_ms?.toFixed(0)}ms
                            {testResults[provider.id].model_tested && ` • ${testResults[provider.id].model_tested}`}
                          </p>
                        ) : (
                          <p className="text-xs text-red-600 mt-1 break-words">
                            {testResults[provider.id].error || "Error desconocido"}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(testResults[provider.id].tested_at).toLocaleString("es-ES", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => handleTestConnection(provider.id)}
                    disabled={testingProvider === provider.id}
                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    {testingProvider === provider.id ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    Test
                  </button>

                  <button
                    onClick={() => handleEdit(provider)}
                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Editar
                  </button>

                  {!provider.is_default && (
                    <button
                      onClick={() => handleSetDefault(provider.id)}
                      className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-sm"
                    >
                      Marcar por defecto
                    </button>
                  )}

                  <button
                    onClick={() => handleDelete(provider.id, provider.display_name)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-sm"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modelos por rol (propuestas) */}
      {providers.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Modelos por rol</h2>
          <p className="text-sm text-gray-600 mb-4">
            Asigna un proveedor/modelo distinto a cada rol del flujo de propuestas. Si el
            proveedor asignado falla, se usa la cadena por defecto (failover intacto).
          </p>
          <LLMRoleAssignments
            providers={providers.map((p) => ({
              id: p.id,
              display_name: p.display_name,
              default_model: p.default_model,
              is_active: p.is_active,
            }))}
          />
        </div>
      )}

      {/* Estadísticas de Uso */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Estadísticas de Uso</h2>
        {isLoadingStats ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">Cargando estadísticas...</p>
          </div>
        ) : usageStats ? (
          <LLMUsageChart
            usageData={usageStats.usage_data}
            providerSummaries={usageStats.provider_summaries}
            periodDays={usageStats.period_days}
          />
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">No hay estadísticas disponibles</p>
          </div>
        )}
      </div>

      {/* Form Dialog - Create */}
      <LLMProviderForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleCreateProvider}
        mode="create"
      />

      {/* Form Dialog - Edit */}
      {editingProvider && (
        <LLMProviderForm
          isOpen={!!editingProvider}
          onClose={() => setEditingProvider(null)}
          onSubmit={handleUpdateProvider}
          mode="edit"
          initialData={{
            provider_name: editingProvider.provider_name,
            display_name: editingProvider.display_name,
            api_key: "", // No mostramos la API key existente por seguridad
            api_base_url: editingProvider.api_base_url,
            default_model: editingProvider.default_model,
            temperature_default: editingProvider.temperature_default,
            rate_limit_rpm: editingProvider.rate_limit_rpm,
            monthly_budget_usd: editingProvider.monthly_budget_usd,
            priority: editingProvider.priority,
            is_active: editingProvider.is_active,
          }}
        />
      )}
    </div>
  );
}
